package diana.soleil.design.iraniancanadienbazzar.feature.events

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.TicketRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.Ticket
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketType
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** Ticket claim for one free event (Stitch `78431a7c`, free-RSVP variant). */
data class EventTicketsUiState(
    val loading: Boolean = true,
    val event: Event? = null,
    val types: List<TicketType> = emptyList(),
    val selectedTypeId: String? = null,
    val qty: Int = 1,
    val claiming: Boolean = false,
    val signInRequired: Boolean = false,
    val failed: Boolean = false,
    /** Set on success — the route navigates to the digital ticket. */
    val issuedTicketId: String? = null,
)

class EventTicketsViewModel(
    private val eventRepository: EventRepository,
    private val ticketRepository: TicketRepository,
    private val analytics: AnalyticsTracker,
    private val eventId: String,
) : ViewModel() {

    private val progress = MutableStateFlow(EventTicketsUiState(loading = false))
    private val eventFlow = MutableStateFlow<Event?>(null)
    // True once a load attempt failed — lets the screen leave the spinner and show a real Retry.
    private val loadFailed = MutableStateFlow(false)

    val state: StateFlow<EventTicketsUiState> =
        combine(ticketRepository.observeTicketTypes(eventId), eventFlow, progress, loadFailed) { types, event, p, failed ->
            val list = (types as? DataResult.Success)?.data.orEmpty()
            p.copy(
                loading = event == null && !failed,
                event = event,
                types = list,
                // Auto-select when there's exactly one claimable type.
                selectedTypeId = p.selectedTypeId ?: list.singleOrNull { !it.soldOut }?.id,
            )
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), EventTicketsUiState())

    init {
        load()
    }

    /** (Re)fetches the event; wired to the error-state Retry so it actually re-attempts the load. */
    fun load() {
        viewModelScope.launch {
            loadFailed.value = false
            when (val result = eventRepository.getEvent(eventId)) {
                is DataResult.Success -> eventFlow.value = result.data
                is DataResult.Failure -> loadFailed.value = true
            }
        }
    }

    fun select(typeId: String) {
        progress.value = progress.value.copy(selectedTypeId = typeId)
    }

    fun setQty(qty: Int) {
        progress.value = progress.value.copy(qty = qty.coerceIn(1, 4))
    }

    fun claim() {
        val p = progress.value
        val typeId = state.value.selectedTypeId ?: return
        if (p.claiming) return
        viewModelScope.launch {
            progress.value = p.copy(claiming = true, failed = false, signInRequired = false)
            when (val result = ticketRepository.issueFreeTicket(eventId, typeId, state.value.qty)) {
                is DataResult.Success -> {
                    analytics.track(
                        AnalyticsEvents.TICKET_CLAIMED,
                        mapOf(AnalyticsParams.EVENT_ID to eventId),
                    )
                    progress.value = progress.value.copy(claiming = false, issuedTicketId = result.data)
                }
                is DataResult.Failure -> progress.value = progress.value.copy(
                    claiming = false,
                    signInRequired = result.error is AppError.Unauthorized,
                    failed = result.error !is AppError.Unauthorized,
                )
            }
        }
    }

    fun consumeGates() {
        progress.value = progress.value.copy(signInRequired = false, issuedTicketId = null)
    }
}

/** The user's ticket wallet (Stitch `c162e3ff` per ticket). */
data class MyTicketsUiState(
    val loading: Boolean = true,
    val tickets: List<Ticket> = emptyList(),
    val signInRequired: Boolean = false,
    val error: Boolean = false,
)

@OptIn(ExperimentalCoroutinesApi::class)
class MyTicketsViewModel(
    ticketRepository: TicketRepository,
) : ViewModel() {
    // Bumped by retry() to re-subscribe the wallet stream after a load failure.
    private val reloadKey = MutableStateFlow(0)

    val state: StateFlow<MyTicketsUiState> =
        reloadKey.flatMapLatest { ticketRepository.observeMyTickets() }.map { result ->
            when (result) {
                is DataResult.Success -> MyTicketsUiState(loading = false, tickets = result.data)
                is DataResult.Failure -> MyTicketsUiState(
                    loading = false,
                    signInRequired = result.error is AppError.Unauthorized,
                    error = result.error !is AppError.Unauthorized,
                )
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), MyTicketsUiState())

    /** Re-subscribes the wallet stream after a load failure (wired to the error-state Retry). */
    fun retry() {
        reloadKey.value += 1
    }
}
